import React, { useState } from "react";
import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Button } from "@material-ui/core";

// Define CustomWidthTooltip
const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "15rem",
    fontSize: "1rem",
    color: "black",
    textAlign: "center",
    background: "white",
    border: "0.5px solid lightgrey",
  },
});

// Helper function to convert RGB/RGBA to hex
function rgbToHex(rgb) {
  if (!rgb || rgb === 'transparent' || rgb === 'rgba(0, 0, 0, 0)' || rgb === 'rgba(0,0,0,0)') {
    return undefined;
  }
  
  // Handle rgb/rgba format
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (match) {
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    
    // Validate RGB values are in valid range
    if (isNaN(r) || isNaN(g) || isNaN(b) || r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
      return undefined;
    }
    
    const rHex = r.toString(16).padStart(2, '0').toUpperCase();
    const gHex = g.toString(16).padStart(2, '0').toUpperCase();
    const bHex = b.toString(16).padStart(2, '0').toUpperCase();
    return rHex + gHex + bHex;
  }
  
  // If already hex format, remove # if present and validate
  if (rgb.startsWith('#')) {
    const hex = rgb.substring(1).toUpperCase();
    // Validate it's a 6-digit hex code
    if (/^[0-9A-F]{6}$/.test(hex)) {
      return hex;
    }
  } else if (/^[0-9A-Fa-f]{6}$/.test(rgb)) {
    // Already a valid hex without #
    return rgb.toUpperCase();
  }
  
  return undefined;
}

// Helper function to convert cm to inches (PptxGenJS uses inches internally)
function cmToInches(cm) {
  return cm / 2.54;
}

// Helper function to load image as base64 with dimensions
function loadImageAsBase64(imagePath) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        // Always convert to PNG for better compatibility with PptxGenJS
        const dataUrl = canvas.toDataURL('image/png');
        if (!dataUrl || dataUrl === 'data:,') {
          throw new Error('Failed to convert image to base64');
        }
        console.log(`Successfully loaded image: ${imagePath}, size: ${img.width}x${img.height}`);
        resolve({ dataUrl, width: img.width, height: img.height });
      } catch (error) {
        console.error(`Error converting image ${imagePath} to base64:`, error);
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error(`Error loading image ${imagePath}:`, error);
      console.error(`Image src attempted: ${imagePath}`);
      reject(new Error(`Failed to load image: ${imagePath}`));
    };
    
    // Set src after event handlers are attached
    img.src = imagePath;
  });
}

// Cache for loaded images to avoid reloading for each slide
let masterSlideImagesCache = null;

// Azure Blob Storage base URL for PPTX slide images
const BLOB_STORAGE_BASE_URL = 'https://skygeniwebapp.blob.core.windows.net/pptx-slides';

// Function to load master slide images (called once)
async function loadMasterSlideImages() {
  if (masterSlideImagesCache) {
    return masterSlideImagesCache;
  }
  
  try {
    const results = await Promise.allSettled([
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/Top Right.png`),
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/Bottom Left.png`),
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/logo@3x.webp`),
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/Top Right line.png`),
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/powerdby.png`)
    ]);
    
    const topRightImage = results[0].status === 'fulfilled' ? results[0].value.dataUrl : null;
    const bottomLeftImage = results[1].status === 'fulfilled' ? results[1].value.dataUrl : null;
    const logoImage = results[2].status === 'fulfilled' ? results[2].value.dataUrl : null;
    const topRightLineResult = results[3].status === 'fulfilled' ? results[3].value : null;
    const topRightLineImage = topRightLineResult ? topRightLineResult.dataUrl : null;
    const topRightLineDimensions = topRightLineResult ? { width: topRightLineResult.width, height: topRightLineResult.height } : null;
    const powerdbyResult = results[4].status === 'fulfilled' ? results[4].value : null;
    const powerdbyImage = powerdbyResult ? powerdbyResult.dataUrl : null;
    const powerdbyDimensions = powerdbyResult ? { width: powerdbyResult.width, height: powerdbyResult.height } : null;
    
    if (results[0].status === 'rejected') {
      console.error('Failed to load Top Right.png:', results[0].reason);
    }
    if (results[1].status === 'rejected') {
      console.error('Failed to load Bottom Left.png:', results[1].reason);
    }
    if (results[2].status === 'rejected') {
      console.error('Failed to load logo@3x.webp:', results[2].reason);
    }
    if (results[3].status === 'rejected') {
      console.error('Failed to load Top Right line.png:', results[3].reason);
    }
    if (results[4].status === 'rejected') {
      console.error('Failed to load powerdby.png:', results[4].reason);
    }
    
    masterSlideImagesCache = { 
      topRightImage, 
      bottomLeftImage, 
      logoImage, 
      topRightLineImage,
      topRightLineDimensions,
      powerdbyImage,
      powerdbyDimensions
    };
    console.log('Master slide images loaded:', {
      topRight: !!topRightImage,
      bottomLeft: !!bottomLeftImage,
      logo: !!logoImage,
      topRightLine: !!topRightLineImage,
      topRightLineDimensions,
      powerdby: !!powerdbyImage,
      powerdbyDimensions
    });
    return masterSlideImagesCache;
  } catch (error) {
    console.error('Error loading master slide images:', error);
    masterSlideImagesCache = { 
      topRightImage: null, 
      bottomLeftImage: null, 
      logoImage: null, 
      topRightLineImage: null,
      topRightLineDimensions: null,
      powerdbyImage: null,
      powerdbyDimensions: null
    };
    return masterSlideImagesCache;
  }
}

// Function to extract table data from DOM element with full styling
// This extracts all styles dynamically and creates a proper PPTX table (not an image)
function extractTableData(tableElement) {
  if (!tableElement) return null;
  
  const tableRows = Array.from(tableElement.querySelectorAll('tr'));
  const tableData = [];

  // Process each row
  tableRows.forEach((row) => {
    const cells = Array.from(row.querySelectorAll('th, td'));
    const rowData = [];
    
    // Get row-level styling
    const rowComputedStyle = window.getComputedStyle(row);
    const rowBgColor = rowComputedStyle.backgroundColor;
    
    cells.forEach((cell) => {
      const cellText = cell.textContent.trim().replace(/\s+/g, ' ');
      // Get colspan - check both lowercase and camelCase (Material-UI uses colSpan)
      const colSpanAttr = cell.getAttribute('colspan') || cell.getAttribute('colSpan') || cell.colSpan || '1';
      const colSpan = parseInt(colSpanAttr) || 1;
      // Get rowspan - check both lowercase and camelCase
      const rowSpanAttr = cell.getAttribute('rowspan') || cell.getAttribute('rowSpan') || cell.rowSpan || '1';
      const rowSpan = parseInt(rowSpanAttr) || 1;
      
      // Get cell styling
      const computedStyle = window.getComputedStyle(cell);
      const bgColor = computedStyle.backgroundColor;
      const textColor = computedStyle.color;
      const fontWeight = computedStyle.fontWeight;
      const fontStyle = computedStyle.fontStyle;
      const fontSize = computedStyle.fontSize;
      const fontFamily = computedStyle.fontFamily;
      const textAlign = computedStyle.textAlign || 'left';
      const verticalAlign = computedStyle.verticalAlign || 'middle';
      
      // Border styles
      const borderTopColor = computedStyle.borderTopColor;
      const borderRightColor = computedStyle.borderRightColor;
      const borderBottomColor = computedStyle.borderBottomColor;
      const borderLeftColor = computedStyle.borderLeftColor;
      const borderTopWidth = parseFloat(computedStyle.borderTopWidth) || 0;
      const borderRightWidth = parseFloat(computedStyle.borderRightWidth) || 0;
      const borderBottomWidth = parseFloat(computedStyle.borderBottomWidth) || 0;
      const borderLeftWidth = parseFloat(computedStyle.borderLeftWidth) || 0;
      
      // Padding
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      
      // Build cell object for pptxgenjs
      // Ensure text is always a string
      const cellObj = {
        text: String(cellText || '').replace(/\0/g, ''), // Remove null characters that can corrupt PPTX
      };
      
      // Add options
      const options = {};
      
      // Background color - use cell color, fallback to row color, include all colors including white
      let finalBgColor = bgColor;
      if ((!bgColor || bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'rgba(0,0,0,0)' || bgColor === 'transparent') && rowBgColor && rowBgColor !== 'rgba(0, 0, 0, 0)' && rowBgColor !== 'rgba(0,0,0,0)' && rowBgColor !== 'transparent') {
        finalBgColor = rowBgColor;
      }
      const hexBg = rgbToHex(finalBgColor);
      // Include ALL background colors, including white (FFFFFF), to preserve exact styling
      // Only skip if it's truly transparent or invalid
      if (hexBg) {
        options.fill = hexBg;
      }
      
      // Text color - include all colors including black
      const hexText = rgbToHex(textColor);
      if (hexText) {
        options.color = hexText;
      }
      
      // Font weight (Bold)
      const isBold = fontWeight === 'bold' || parseInt(fontWeight) >= 600;
      if (isBold) {
        options.bold = true;
      }
      
      // Font style (Italic)
      if (fontStyle === 'italic') {
        options.italic = true;
      }
      
      // Font size (convert px to pt)
      if (fontSize) {
        const fontSizePt = Math.round(parseFloat(fontSize) * 0.75);
        if (fontSizePt > 0) {
          options.fontSize = fontSizePt;
        }
      }
      
      // Font family (use first font from font stack)
      if (fontFamily && fontFamily !== 'initial') {
        const firstFont = fontFamily.split(',')[0].replace(/['"]/g, '').trim();
        if (firstFont) {
          options.fontFace = firstFont;
        }
      }
      
      // Horizontal alignment
      if (textAlign === 'center') {
        options.align = 'center';
      } else if (textAlign === 'right') {
        options.align = 'right';
      } else {
        options.align = 'left';
      }
      
      // Vertical alignment
      if (verticalAlign === 'top') {
        options.valign = 'top';
      } else if (verticalAlign === 'bottom') {
        options.valign = 'bottom';
      } else {
        options.valign = 'middle';
      }
      
      // Colspan - always include if it's greater than 1
      // PptxGenJS requires colspan to be explicitly set for spanning cells
      // Note: PptxGenJS only needs colspan when > 1, but we'll include it for clarity
      if (colSpan > 1 && colSpan <= 100) {
        options.colspan = colSpan;
      }
      
      // Rowspan - DISABLED: PptxGenJS rowspan can cause corruption, skip it
      // if (rowSpan > 1 && rowSpan < 100) {
      //   options.rowspan = rowSpan;
      // }
      
      // Add cell-level borders if present
      // Check if cell has any visible borders
      const hasBorder = (borderTopWidth > 0 && borderTopColor && borderTopColor !== 'rgba(0, 0, 0, 0)' && borderTopColor !== 'transparent') ||
                        (borderRightWidth > 0 && borderRightColor && borderRightColor !== 'rgba(0, 0, 0, 0)' && borderRightColor !== 'transparent') ||
                        (borderBottomWidth > 0 && borderBottomColor && borderBottomColor !== 'rgba(0, 0, 0, 0)' && borderBottomColor !== 'transparent') ||
                        (borderLeftWidth > 0 && borderLeftColor && borderLeftColor !== 'rgba(0, 0, 0, 0)' && borderLeftColor !== 'transparent');
      
      if (hasBorder) {
        // Get the most common border color and width
        const borderColors = [];
        const borderWidths = [];
        
        if (borderTopWidth > 0 && borderTopColor && borderTopColor !== 'rgba(0, 0, 0, 0)' && borderTopColor !== 'transparent') {
          const hex = rgbToHex(borderTopColor);
          if (hex) borderColors.push(hex);
          borderWidths.push(borderTopWidth);
        }
        if (borderRightWidth > 0 && borderRightColor && borderRightColor !== 'rgba(0, 0, 0, 0)' && borderRightColor !== 'transparent') {
          const hex = rgbToHex(borderRightColor);
          if (hex) borderColors.push(hex);
          borderWidths.push(borderRightWidth);
        }
        if (borderBottomWidth > 0 && borderBottomColor && borderBottomColor !== 'rgba(0, 0, 0, 0)' && borderBottomColor !== 'transparent') {
          const hex = rgbToHex(borderBottomColor);
          if (hex) borderColors.push(hex);
          borderWidths.push(borderBottomWidth);
        }
        if (borderLeftWidth > 0 && borderLeftColor && borderLeftColor !== 'rgba(0, 0, 0, 0)' && borderLeftColor !== 'transparent') {
          const hex = rgbToHex(borderLeftColor);
          if (hex) borderColors.push(hex);
          borderWidths.push(borderLeftWidth);
        }
        
        if (borderColors.length > 0 && borderWidths.length > 0) {
          // Use the first border color found (most common approach)
          const cellBorderColor = borderColors[0];
          const avgBorderWidth = borderWidths.reduce((sum, w) => sum + w, 0) / borderWidths.length;
          const cellBorderWidth = Math.max(0.5, Math.min(5, Math.round(avgBorderWidth * 0.75))); // Convert px to pt, clamp between 0.5-5pt
          
          if (cellBorderColor && /^[0-9A-Fa-f]{6}$/i.test(cellBorderColor)) {
            options.border = {
              type: "solid",
              color: cellBorderColor.toUpperCase(),
              pt: cellBorderWidth
            };
          }
        }
      }
      
      // Validate and clean options before adding
      // Remove any undefined, null, or invalid values
      const cleanOptions = {};
      Object.keys(options).forEach(key => {
        const value = options[key];
        if (value !== undefined && value !== null) {
          // Validate specific types
          if (key === 'fontSize') {
            if (typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0 && value <= 400) {
              cleanOptions[key] = Math.round(value);
            }
            return;
          }
          if (key === 'colspan') {
            if (typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0 && value <= 100) {
              cleanOptions[key] = Math.round(value);
            }
            return;
          }
          if (key === 'rowspan') {
            if (typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0 && value <= 100) {
              cleanOptions[key] = Math.round(value);
            }
            return;
          }
          if (key === 'fill' || key === 'color') {
            // Validate hex color - include all valid hex including FFFFFF (white)
            if (typeof value === 'string' && /^[0-9A-Fa-f]{6}$/i.test(value)) {
              cleanOptions[key] = value.toUpperCase();
            }
            return;
          }
          // For all other options, add them as-is if they're valid
          cleanOptions[key] = value;
        }
      });
      
      // Always add options if there are any valid ones
      // PptxGenJS needs the options object to apply cell styles
      if (Object.keys(cleanOptions).length > 0) {
        cellObj.options = cleanOptions;
      }
      
      rowData.push(cellObj);
    });
    
    if (rowData.length > 0) {
      tableData.push(rowData);
    }
  });

  // Validate table structure - ensure all rows have consistent column counts
  if (tableData.length === 0) return null;
  
  // Clean and validate each cell
  const cleanedTableData = tableData.map(row => {
    return row.map(cell => {
      // Ensure cell is an object
      if (!cell || typeof cell !== 'object') {
        return { text: '' };
      }
      
      // Clean cell object
      const cleanedCell = {
        text: String(cell.text || '').replace(/\0/g, '').substring(0, 10000), // Limit text length
      };
      
      // Clean and validate options
      if (cell.options && typeof cell.options === 'object') {
        const cleanedOptions = {};
        
        // Validate each option
        Object.keys(cell.options).forEach(key => {
          const value = cell.options[key];
          
          // Skip null, undefined
          if (value === null || value === undefined) {
            return;
          }
          
          switch (key) {
            case 'fill':
            case 'color':
              // Validate hex color - include all colors including white (FFFFFF)
              if (typeof value === 'string' && /^[0-9A-Fa-f]{6}$/i.test(value)) {
                cleanedOptions[key] = value.toUpperCase();
              }
              break;
            case 'fontSize':
              // Validate font size (1-400) - skip NaN check for numbers
              if (typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0 && value <= 400) {
                cleanedOptions[key] = Math.round(value);
              }
              break;
            case 'colspan':
              // Validate colspan (1-100) - keep it enabled
              if (typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0 && value <= 100) {
                cleanedOptions[key] = Math.round(value);
              }
              break;
            case 'rowspan':
              // Rowspan - DISABLED: Can cause corruption in PptxGenJS
              // Skip rowspan to prevent file corruption
              break;
            case 'bold':
            case 'italic':
              // Boolean values
              if (typeof value === 'boolean') {
                cleanedOptions[key] = value;
              }
              break;
            case 'align':
              // Alignment values
              if (['left', 'center', 'right'].includes(value)) {
                cleanedOptions[key] = value;
              }
              break;
            case 'valign':
              // Vertical alignment values
              if (['top', 'middle', 'bottom'].includes(value)) {
                cleanedOptions[key] = value;
              }
              break;
            case 'fontFace':
              // Font family (string, max 50 chars)
              if (typeof value === 'string' && value.length > 0 && value.length <= 50) {
                cleanedOptions[key] = value.substring(0, 50);
              }
              break;
            case 'border':
              // Border object: { type: "solid", color: "CCCCCC", pt: 1 }
              if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                const borderType = value.type === 'solid' ? 'solid' : 'solid'; // Default to solid
                const borderColor = (typeof value.color === 'string' && /^[0-9A-Fa-f]{6}$/i.test(value.color)) 
                  ? value.color.toUpperCase() 
                  : 'CCCCCC'; // Default gray
                const borderPt = (typeof value.pt === 'number' && !isNaN(value.pt) && isFinite(value.pt) && value.pt > 0 && value.pt <= 10)
                  ? Math.max(0.5, Math.min(10, value.pt))
                  : 1; // Default 1pt
                
                cleanedOptions[key] = {
                  type: borderType,
                  color: borderColor,
                  pt: borderPt
                };
              }
              break;
            case 'margin':
              // Margin - DISABLED: Can cause corruption, skip it
              // PptxGenJS margin support is unreliable
              break;
            default:
              // Skip unknown options to prevent corruption
              // Only include explicitly supported PptxGenJS options
              break;
          }
        });
        
        // Always add options if there are any - PptxGenJS needs options object for styling
        if (Object.keys(cleanedOptions).length > 0) {
          cleanedCell.options = cleanedOptions;
        }
      }
      
      return cleanedCell;
    });
  });
  
  // Calculate the maximum number of columns (accounting for colspan)
  let maxCols = 0;
  cleanedTableData.forEach(row => {
    let rowCols = 0;
    row.forEach(cell => {
      rowCols += (cell.options?.colspan || 1);
    });
    maxCols = Math.max(maxCols, rowCols);
  });
  
  // Ensure all rows have the same number of columns
  // If a row has fewer columns, pad with empty cells
  const validatedTableData = cleanedTableData.map(row => {
    let rowCols = 0;
    row.forEach(cell => {
      rowCols += (cell.options?.colspan || 1);
    });
    
    if (rowCols < maxCols) {
      // Add empty cells to match column count
      const paddedRow = [...row];
      for (let i = rowCols; i < maxCols; i++) {
        paddedRow.push({ text: '' });
      }
      return paddedRow;
    }
    
    return row;
  });
  
  return validatedTableData.length > 0 ? validatedTableData : null;
}

function GlobalDownloadButton({ dashboardTitle, fileName }) {
  const [tooltipText, setTooltipText] = useState(
    "Download all charts and tables as PowerPoint"
  );
  const [loading, setLoading] = useState(false);

  // Consistent title styling for all slides - Montserrat ExtraBold, size 20
  const titleStyle = {
    x: cmToInches(0.508), // Left margin: 0.508cm (0.2 inches)
    y: cmToInches(0.5), // Consistent position from top: 0.5 cm (0.197 inches)
    w: cmToInches(22.86), // Width: 9 inches = 22.86 cm
    h: cmToInches(1.5), // Height for font size 20: 1.5 cm (0.591 inches)
    fontSize: 20, // Chart title font size
    fontFace: "Montserrat ExtraBold", // Chart title font
    align: "left",
    bold: true,
    color: "000000", // Black text
    textWrap: false,
  };

  const addImageWithTitleToSlide = (
    slide,
    imageBase64,
    imageWidth,
    imageHeight,
    title
  ) => {
    // All measurements in cm, converted to inches for PptxGenJS
    const slideWidthCm = 19.05; // 7.5 inches
    const slideHeightCm = 11.7475; // 4.625 inches

    const imageAspectRatio = imageWidth / imageHeight;
    const slideAspectRatio = slideWidthCm / slideHeightCm;

    let widthCm;
    let heightCm;

    if (imageAspectRatio > slideAspectRatio) {
      widthCm = slideWidthCm;
      heightCm = widthCm / imageAspectRatio;
    } else {
      heightCm = slideHeightCm;
      widthCm = heightCm * imageAspectRatio;
    }

    const xPos = (slideWidthCm - widthCm) / 2;
    const yPos = (slideHeightCm - heightCm) / 2;

    // Add title to the slide using consistent styling
    slide.addText(title, titleStyle);

    // Add image to the slide - positioned below title with consistent spacing
    // Title ends at 0.5 + 1.5 = 2.0 cm, image starts at 2.3 cm with 0.3 cm spacing
    const titleEndY = 0.5 + 1.5; // Title y + title height
    const imageStartY = titleEndY + 0.3; // Add 0.3 cm spacing below title
    
    // Recalculate image position to fit below title
    const availableHeight = slideHeightCm - imageStartY - 0.5; // Leave 0.5 cm bottom margin
    let adjustedHeightCm = heightCm;
    let adjustedWidthCm = widthCm;
    
    if (heightCm > availableHeight) {
      adjustedHeightCm = availableHeight;
      adjustedWidthCm = adjustedHeightCm * imageAspectRatio;
    }
    
    const adjustedXPos = (slideWidthCm - adjustedWidthCm) / 2;
    
    slide.addImage({
      data: imageBase64,
      x: cmToInches(adjustedXPos + 3.048), // 1.2 inches = 3.048 cm offset like ExportPPT.js
      y: cmToInches(imageStartY), // Fixed position below title: 2.3 cm
      w: cmToInches(adjustedWidthCm),
      h: cmToInches(adjustedHeightCm),
    });
  };

  const handleExport = async () => {
    setLoading(true);
    setTooltipText("Exporting all charts and tables...");

    try {
      // Create the PowerPoint presentation
      const pptx = new PptxGenJS();

      // Load master slide images first
      const masterImages = await loadMasterSlideImages();
      
      // Build master slide objects array
      // All measurements are in cm, converted to inches for PptxGenJS (which uses inches internally)
      const masterObjects = [];

      // Top Horizontal Line (using Top Right line.png image)
      // Position: extends across the top portion of the slide, aligned with header area
      // The image contains both the "#BeBreachReady" text and the horizontal line
      if (masterImages.topRightLineImage && masterImages.topRightLineDimensions) {
        // Calculate dimensions maintaining aspect ratio
        const desiredWidthCm = 18.00; // Desired width in cm
        const maxHeightCm = 0.5; // Maximum height in cm to prevent it from being too tall
        
        const imageAspectRatio = masterImages.topRightLineDimensions.width / masterImages.topRightLineDimensions.height;
        
        // Calculate height based on desired width and aspect ratio
        let finalWidthCm = desiredWidthCm;
        let finalHeightCm = desiredWidthCm / imageAspectRatio;
        
        // If calculated height exceeds max, adjust width to fit max height
        if (finalHeightCm > maxHeightCm) {
          finalHeightCm = maxHeightCm;
          finalWidthCm = maxHeightCm * imageAspectRatio;
        }
        
        masterObjects.push({
          image: {
            data: masterImages.topRightLineImage,
            x: cmToInches(0.508), // Start from left margin, aligned with header text area
            y: cmToInches(0.381), // Position at top of slide
            w: cmToInches(finalWidthCm), // Width maintaining aspect ratio
            h: cmToInches(finalHeightCm), // Height maintaining aspect ratio
          }
        });
      } else if (masterImages.topRightLineImage) {
        // Fallback if dimensions are not available
        masterObjects.push({
          image: {
            data: masterImages.topRightLineImage,
            x: cmToInches(0.508),
            y: cmToInches(0.381),
            w: cmToInches(18.00),
            h: cmToInches(0.235),
          }
        });
      }
      
      // Top Right Pattern Image
      // Position: x=17.78cm, y=0cm, w=7.62cm, h=6.35cm
      if (masterImages.topRightImage) {
        masterObjects.push({
          image: {
            data: masterImages.topRightImage,
            x: cmToInches(17.78),
            y: 0.0,
            w: cmToInches(7.62),
            h: cmToInches(6.35),
          }
        });
      }
      
      // Bottom Left Pattern Image (positioned first, behind logo)
      // Standard slide height is 19.05 cm (7.5 inches)
      // Position: x=0cm, y=14.224cm, w=6.35cm, h=4.826cm (adjusted to fit within slide)
      if (masterImages.bottomLeftImage) {
        masterObjects.push({
          image: {
            data: masterImages.bottomLeftImage,
            x: 0.0,
            y: cmToInches(9.50), // Adjusted to fit within slide
            w: cmToInches(6.35),
            h: cmToInches(4.826), // Reduced to ensure it fits
          }
        });
      }
      
      // Bottom Left Logo (positioned above the pattern)
      // Standard slide height is 19.05 cm (7.5 inches)
      // Position: x=0.508cm, y=16.51cm, w=5.08cm, h=1.016cm (positioned higher to fit within slide)
      if (masterImages.logoImage) {
        masterObjects.push({
          image: {
            data: masterImages.logoImage,
            x: cmToInches(0.508),
            y: cmToInches(13.50), // 6.5 inches from top, ensuring it fits within 7.5 inch slide
            w: cmToInches(1.90),
            h: cmToInches(0.300),
          }
        });
      }
      
      // Bottom Right Copyright Text
      // Position: x=10.16cm, y=16.51cm, w=13.97cm, h=0.635cm (aligned with logo)
      masterObjects.push({
        text: {
          text: `© ${new Date().getFullYear()} ColorTokens, Inc. Confidential and Proprietary`,
          options: {
            x: cmToInches(10.16),
            y: cmToInches(13.50), // Aligned with logo at 6.5 inches from top
            w: cmToInches(13.97),
            h: cmToInches(0.635),
            fontSize: 6,
            color: '7F7F80',
            align: 'right',
            valign: 'bottom',
          }
        }
      });
      
      // Bottom Center Powered By Image
      // Position: centered at bottom, aligned with copyright and logo
      if (masterImages.powerdbyImage && masterImages.powerdbyDimensions) {
        // Calculate dimensions maintaining aspect ratio
        const desiredHeightCm = 0.3; // Desired height in cm (reduced size)
        const maxWidthCm = 3.0; // Maximum width in cm (reduced size)
        
        const imageAspectRatio = masterImages.powerdbyDimensions.width / masterImages.powerdbyDimensions.height;
        
        // Calculate width based on desired height and aspect ratio
        let finalHeightCm = desiredHeightCm;
        let finalWidthCm = desiredHeightCm * imageAspectRatio;
        
        // If calculated width exceeds max, adjust height to fit max width
        if (finalWidthCm > maxWidthCm) {
          finalWidthCm = maxWidthCm;
          finalHeightCm = maxWidthCm / imageAspectRatio;
        }
        
        // Center horizontally: (slideWidth - imageWidth) / 2
        // Slide width is 25.4 cm (10 inches)
        const slideWidthCm = 25.4;
        const centerX = (slideWidthCm - finalWidthCm) / 2;
        
        masterObjects.push({
          image: {
            data: masterImages.powerdbyImage,
            x: cmToInches(centerX),
            y: cmToInches(13.50), // Aligned with logo and copyright at 6.5 inches from top
            w: cmToInches(finalWidthCm),
            h: cmToInches(finalHeightCm),
          }
        });
      } else if (masterImages.powerdbyImage) {
        // Fallback if dimensions are not available
        const slideWidthCm = 25.4;
        const imageWidthCm = 2.0; // Default width (reduced size)
        const centerX = (slideWidthCm - imageWidthCm) / 2;
        
        masterObjects.push({
          image: {
            data: masterImages.powerdbyImage,
            x: cmToInches(centerX),
            y: cmToInches(13.50),
            w: cmToInches(imageWidthCm),
            h: cmToInches(0.3), // Reduced height
          }
        });
      }
      
      // Define master slide template
      pptx.defineSlideMaster({
        title: 'COLORTOKENS_MASTER',
        background: { color: 'FFFFFF' },
        objects: masterObjects,
        slideNumber: {
          x: cmToInches(24.384),
          y: cmToInches(13.50), // Aligned with logo and copyright at 6.5 inches from top
          w: cmToInches(0.762),
          h: cmToInches(0.635),
          color: '7F7F80',
          fontSize: 6,
          align: 'right',
          valign: 'bottom',
        },
      });

      // Create cover slide as the first slide
      try {
        // Load coverslide.png for the cover slide
        const coverSlideImage = await loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/coverslide.png`);
        
        const coverSlide = pptx.addSlide();
        const slideWidthCm = 25.4; // 10 inches
        const slideHeightCm = 19.05; // 7.5 inches
        
        // Background: White
        coverSlide.background = { color: 'FFFFFF' };
        
        // Top Right Pattern Image (background)
        if (masterImages.topRightImage) {
          coverSlide.addImage({
            data: masterImages.topRightImage,
            x: cmToInches(17.78),
            y: 0.0,
            w: cmToInches(7.62),
            h: cmToInches(6.35),
          });
        }
        
        // Coverslide.png at Top Left (instead of logo and Top Right line)
        if (coverSlideImage && coverSlideImage.dataUrl) {
          // Calculate dimensions maintaining aspect ratio
          const imageAspectRatio = coverSlideImage.width / coverSlideImage.height;
          const maxWidthCm = 8.0; // Maximum width for top left
          const maxHeightCm = 2.5; // Maximum height for top left
          
          let finalWidthCm = maxWidthCm;
          let finalHeightCm = maxWidthCm / imageAspectRatio;
          
          // If calculated height exceeds max, adjust width to fit max height
          if (finalHeightCm > maxHeightCm) {
            finalHeightCm = maxHeightCm;
            finalWidthCm = maxHeightCm * imageAspectRatio;
          }
          
          coverSlide.addImage({
            data: coverSlideImage.dataUrl,
            x: cmToInches(0.508),
            y: cmToInches(0.5),
            w: cmToInches(finalWidthCm),
            h: cmToInches(finalHeightCm),
          });
        }
        
        // Main Title: "Regional Sales Review Package" - Centered, large, bold
        const mainTitleY = cmToInches(6.0); // Center vertically
        coverSlide.addText("Regional Sales Review Package", {
          x: cmToInches(0.508),
          y: mainTitleY,
          w: cmToInches(24.384),
          h: cmToInches(1.5),
          fontSize: 44,
          fontFace: "Montserrat ExtraBold",
          align: "center",
          bold: true,
          color: "000000",
        });
        
        // Subtitle 1: "[Illustrative schematics – not actual results]" with yellow highlight
        const subtitle1Y = cmToInches(8.0);
        // Add yellow background rectangle
        coverSlide.addShape('rect', {
          x: cmToInches(0.508),
          y: subtitle1Y,
          w: cmToInches(24.384),
          h: cmToInches(0.8),
          fill: { color: "FFFF00" },
          line: { color: "FFFF00", width: 0 },
        });
        // Add text on top
        coverSlide.addText("[Illustrative schematics – not actual results]", {
          x: cmToInches(0.508),
          y: subtitle1Y,
          w: cmToInches(24.384),
          h: cmToInches(0.8),
          fontSize: 16,
          fontFace: "Montserrat",
          align: "center",
          bold: true,
          color: "000000",
        });
        
        // Subtitle 2: "package for each region (US, Europe, India, ASEAN, Australia)" with yellow highlight
        const subtitle2Y = cmToInches(9.0);
        // Add yellow background rectangle
        coverSlide.addShape('rect', {
          x: cmToInches(0.508),
          y: subtitle2Y,
          w: cmToInches(24.384),
          h: cmToInches(0.8),
          fill: { color: "FFFF00" },
          line: { color: "FFFF00", width: 0 },
        });
        // Add text on top
        coverSlide.addText("package for each region (US, Europe, India, ASEAN, Australia)", {
          x: cmToInches(0.508),
          y: subtitle2Y,
          w: cmToInches(24.384),
          h: cmToInches(0.8),
          fontSize: 16,
          fontFace: "Montserrat",
          align: "center",
          bold: true,
          color: "000000",
        });
        
        // Date/Period: "Week xx, Q1"
        const dateY = cmToInches(10.5);
        coverSlide.addText("Week xx, Q1", {
          x: cmToInches(0.508),
          y: dateY,
          w: cmToInches(24.384),
          h: cmToInches(0.6),
          fontSize: 18,
          fontFace: "Montserrat",
          align: "center",
          color: "000000",
        });
        
        // Example text: "(e.g. week 3 of Q1)"
        const exampleY = cmToInches(11.2);
        coverSlide.addText("(e.g. week 3 of Q1)", {
          x: cmToInches(0.508),
          y: exampleY,
          w: cmToInches(24.384),
          h: cmToInches(0.5),
          fontSize: 14,
          fontFace: "Montserrat",
          align: "center",
          color: "000000",
        });
        
        // Bottom Left Pattern Image (background)
        if (masterImages.bottomLeftImage) {
          coverSlide.addImage({
            data: masterImages.bottomLeftImage,
            x: 0.0,
            y: cmToInches(9.50),
            w: cmToInches(6.35),
            h: cmToInches(4.826),
          });
        }
        
        // Copyright notice at bottom right
        coverSlide.addText(`© ${new Date().getFullYear()} ColorTokens, Inc. Confidential and Proprietary`, {
          x: cmToInches(10.16),
          y: cmToInches(13.50),
          w: cmToInches(13.97),
          h: cmToInches(0.635),
          fontSize: 6,
          color: '7F7F80',
          align: 'right',
          valign: 'bottom',
        });
        
        // Page number "1" at bottom right
        coverSlide.addText("1", {
          x: cmToInches(24.384),
          y: cmToInches(13.50),
          w: cmToInches(0.762),
          h: cmToInches(0.635),
          fontSize: 6,
          color: '7F7F80',
          align: 'right',
          valign: 'bottom',
        });
        
        console.log('Cover slide created successfully');
      } catch (coverError) {
        console.error('Error creating cover slide:', coverError);
        // Continue with export even if cover slide fails
      }

      const allContainers = [];
      const processedElements = new Set();

      // Strategy 1: Find all Paper components and extract their content
      // This handles RegionalSalesReviewPackage structure where Papers contain charts and tables
      const paperComponents = document.querySelectorAll('[class*="MuiPaper"]');
      
      paperComponents.forEach((paper) => {
        if (paper.offsetParent === null) return; // Skip hidden papers
        
        // Get Paper title from h5, h6, or Typography
        const paperTitle = paper.querySelector('h5')?.textContent?.trim() ||
                          paper.querySelector('h6')?.textContent?.trim() ||
                          paper.querySelector('[class*="MuiTypography-h5"]')?.textContent?.trim() ||
                          paper.querySelector('[class*="MuiTypography-h6"]')?.textContent?.trim() ||
                          'Paper Section';
        
        // Find all charts in this Paper
        const chartsInPaper = Array.from(paper.querySelectorAll('[id="printChart"]'));
        chartsInPaper.forEach((chart) => {
          if (processedElements.has(chart) || chart.offsetParent === null) return;
          
          processedElements.add(chart);
          // Try to get a more specific title for the chart
          const chartTitle = getTitleFromContainer(chart) || 
                            getTitleFromContainer(chart.closest('[class*="MuiCard"]')) ||
                            `${paperTitle} - Chart`;
          
          allContainers.push({
            element: chart,
            type: 'chart',
            title: chartTitle,
            paperTitle: paperTitle
          });
        });
        
        // Find all tables in this Paper (even if not associated with charts)
        const tablesInPaper = Array.from(paper.querySelectorAll('table'));
        tablesInPaper.forEach((table) => {
          if (processedElements.has(table) || table.offsetParent === null) return;
          
          processedElements.add(table);
          
          // Try to get a more specific title for the table
          // Look for Typography h6 near the table (often used as table headers)
          const tableHeader = table.closest('[class*="MuiGrid"]')?.querySelector('h6') ||
                             table.previousElementSibling?.querySelector('h6') ||
                             table.parentElement?.previousElementSibling?.querySelector('h6');
          const tableTitle = tableHeader?.textContent?.trim() ||
                            `${paperTitle}`;
          
          const tableContainer = table.closest('[class*="MuiTableContainer"]') ||
                               table.closest('[style*="margin"]') ||
                               table.closest('[class*="MuiGrid"]') ||
                               table.parentElement;
          
          if (tableContainer && tableContainer.offsetParent !== null) {
            allContainers.push({
              element: tableContainer,
              tableElement: table,
              type: 'table',
              title: tableTitle,
              paperTitle: paperTitle
            });
          }
        });
      });

      // Strategy 2: Find all Card containers that have DataVisualizer components
      // Each Card contains one chart (with id="printChart") and one table
      // This handles cases where charts are in Cards but not in Papers
      const cardContainers = document.querySelectorAll('[class*="MuiCard"]');
      const cardsWithCharts = [];
      
      // First, identify all Cards that contain charts (and haven't been processed yet)
      cardContainers.forEach((card) => {
        const chartInCard = card.querySelector('[id="printChart"]');
        if (chartInCard && chartInCard.offsetParent !== null && !processedElements.has(chartInCard)) {
          cardsWithCharts.push({
            card: card,
            chart: chartInCard
          });
        }
      });
      
      // Process each Card: export its chart and table
      cardsWithCharts.forEach(({ card, chart }) => {
        if (processedElements.has(chart)) return;
        
        processedElements.add(chart);
        const title = getTitleFromContainer(chart) || 'Chart';
        
        // Add chart
        allContainers.push({
          element: chart,
          type: 'chart',
          title: title
        });
        
        // Find the table in this same Card
        // The table should be in a Grid item that comes after the chart
        const tables = Array.from(card.querySelectorAll('table'));
        
        for (const table of tables) {
          if (table.offsetParent === null) continue;
          if (processedElements.has(table)) continue;
          
          // Get the table's container (but we'll use the table element itself)
          const tableContainer = table.closest('[style*="margin"]') || 
                                table.closest('[class*="MuiGrid"]') ||
                                table.parentElement;
          
          if (tableContainer && tableContainer.offsetParent !== null) {
            processedElements.add(table);
            // Store the container but we'll extract from the table element
            allContainers.push({
              element: tableContainer,
              tableElement: table, // Store reference to actual table element
              type: 'table',
              title: `${title}`
            });
            break; // Found the table for this chart
          }
        }
      });

      // If no printChart elements found, try alternative method
      if (allContainers.length === 0) {
        // Look for all Grid containers with DataVisualizer structure
        const gridContainers = document.querySelectorAll('[class*="MuiGrid-container"]');
        
        gridContainers.forEach((grid) => {
          // Check if this grid contains a chart (has svgContainerStackBar or svgContainerDoughnut)
          const hasChart = grid.querySelector('[id^="svgContainerStackBar"]') || 
                          grid.querySelector('[id^="svgContainerDoughnut"]');
          
          if (hasChart && !processedElements.has(grid)) {
            processedElements.add(grid);
            const title = getTitleFromContainer(grid) || 'Chart';
            
            // The chart container is the one with printChart id or contains the svg containers
            const chartContainer = grid.querySelector('[id="printChart"]') || 
                                  grid.querySelector('[id^="printChart"]') ||
                                  grid;
            
            allContainers.push({
              element: chartContainer,
              type: 'chart',
              title: title
            });
            
            // Look for table in the same grid or nearby
            const table = grid.querySelector('table');
            if (table && table.offsetParent !== null && !processedElements.has(table)) {
              processedElements.add(table);
              const tableContainer = table.closest('[style*="margin"]') || 
                                    table.closest('[class*="MuiGrid"]') ||
                                    table.parentElement;
              
              if (tableContainer && tableContainer.offsetParent !== null) {
                allContainers.push({
                  element: tableContainer,
                  tableElement: table, // Store reference to actual table element
                  type: 'table',
                  title: `${title}`
                });
              }
            }
          }
        });
      }

      // Strategy 3: Find any remaining standalone tables that weren't captured
      // This is a fallback to ensure we don't miss any tables
      const allTables = document.querySelectorAll('table');
      allTables.forEach((table) => {
        if (processedElements.has(table) || table.offsetParent === null) return;
        
        // Check if this table is already in a Paper or Card we've processed
        const paper = table.closest('[class*="MuiPaper"]');
        const card = table.closest('[class*="MuiCard"]');
        
        // If table is in a Paper or Card, it should have been captured already
        // Only process standalone tables
        if (!paper && !card) {
          processedElements.add(table);
          
          // Try to find a title near the table
          const tableHeader = table.previousElementSibling?.querySelector('h6') ||
                             table.previousElementSibling?.querySelector('h5') ||
                             table.parentElement?.previousElementSibling?.querySelector('h6') ||
                             table.parentElement?.previousElementSibling?.querySelector('h5');
          const tableTitle = tableHeader?.textContent?.trim() || 'Table';
          
          const tableContainer = table.closest('[class*="MuiTableContainer"]') ||
                               table.closest('[style*="margin"]') ||
                               table.closest('[class*="MuiGrid"]') ||
                               table.parentElement;
          
          if (tableContainer && tableContainer.offsetParent !== null) {
            allContainers.push({
              element: tableContainer,
              tableElement: table,
              type: 'table',
              title: tableTitle
            });
          }
        }
      });

      if (allContainers.length === 0) {
        alert("No charts or tables found on this page.");
        setLoading(false);
        setTooltipText("No charts or tables found");
        setTimeout(() => {
          setTooltipText("Download all charts and tables as PowerPoint");
        }, 3000);
        return;
      }

      // Sort containers: Maintain Paper order, then charts and tables within each Paper
      const sortedContainers = [];
      
      // If we have Paper-based items, sort by Paper order
      const paperBasedItems = allContainers.filter(c => c.paperTitle);
      const nonPaperItems = allContainers.filter(c => !c.paperTitle);
      
      if (paperBasedItems.length > 0) {
        // Get all Papers in document order
        const allPapers = Array.from(document.querySelectorAll('[class*="MuiPaper"]'));
        const paperOrderMap = new Map();
        allPapers.forEach((paper, index) => {
          const paperTitle = paper.querySelector('h5')?.textContent?.trim() ||
                            paper.querySelector('h6')?.textContent?.trim() ||
                            paper.querySelector('[class*="MuiTypography-h5"]')?.textContent?.trim() ||
                            paper.querySelector('[class*="MuiTypography-h6"]')?.textContent?.trim() ||
                            `Paper-${index}`;
          paperOrderMap.set(paper, { order: index, title: paperTitle });
        });
        
        // Group items by Paper
        const itemsByPaper = new Map();
        paperBasedItems.forEach(item => {
          const paper = item.element.closest('[class*="MuiPaper"]');
          if (paper && paperOrderMap.has(paper)) {
            const paperInfo = paperOrderMap.get(paper);
            if (!itemsByPaper.has(paperInfo.order)) {
              itemsByPaper.set(paperInfo.order, []);
            }
            itemsByPaper.get(paperInfo.order).push(item);
          }
        });
        
        // Sort by Paper order, then within each Paper: charts first, then tables
        const sortedPaperOrders = Array.from(itemsByPaper.keys()).sort((a, b) => a - b);
        sortedPaperOrders.forEach(paperOrder => {
          const paperItems = itemsByPaper.get(paperOrder);
          const charts = paperItems.filter(i => i.type === 'chart');
          const tables = paperItems.filter(i => i.type === 'table');
          
          // Add charts first
          sortedContainers.push(...charts);
          // Then tables
          sortedContainers.push(...tables);
        });
      }
      
      // Add non-Paper items (charts and tables not in Papers)
      if (nonPaperItems.length > 0) {
        const chartItems = nonPaperItems.filter(c => c.type === 'chart');
        const tableItems = nonPaperItems.filter(c => c.type === 'table');
        
        // Pair charts with their tables
        chartItems.forEach((chartItem) => {
          sortedContainers.push(chartItem);
          // Find corresponding table
          const chartTitleBase = chartItem.title.split(' - ')[0];
          const correspondingTable = tableItems.find(t => 
            t.title.includes(chartTitleBase)
          );
          if (correspondingTable) {
            sortedContainers.push(correspondingTable);
            // Remove from tableItems to avoid duplicates
            const tableIndex = tableItems.indexOf(correspondingTable);
            if (tableIndex > -1) {
              tableItems.splice(tableIndex, 1);
            }
          }
        });
        
        // Add any remaining tables
        sortedContainers.push(...tableItems);
      }

      // Export each container as a slide
      for (let i = 0; i < sortedContainers.length; i++) {
        const container = sortedContainers[i];
        
        try {
          // Check if this is a table and export as table (not image)
          if (container.type === 'table') {
            // Use stored tableElement if available, otherwise query for it
            const tableElement = container.tableElement || container.element.querySelector('table');
            if (tableElement) {
              // Extract table data with all styles preserved
              const tableData = extractTableData(tableElement);
              
              // Validate table data before proceeding
              if (tableData && Array.isArray(tableData) && tableData.length > 0) {
                // Ensure all rows are arrays
                const isValidTableData = tableData.every(row => Array.isArray(row) && row.length > 0);
                
                if (!isValidTableData) {
                  console.warn('Invalid table structure, skipping table export');
                  continue; // Skip this table
                }
                // Create a slide with master slide template for the table
                const tableSlide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });

                // Added Table style format controll

                // Extract original column widths and styling from the table dynamically
                const tableRows = Array.from(tableElement.querySelectorAll('tr'));
                const firstRow = tableRows[0];
                const originalColWidths = [];
                let totalOriginalWidth = 0;
                let tableFontSize = null; // Will be extracted dynamically
                let tableBorderColor = null; // Will be extracted dynamically
                let tableBorderWidth = null; // Will be extracted dynamically
                
                // Get table-level styling dynamically
                const tableComputedStyle = window.getComputedStyle(tableElement);
                const tableFontSizePx = parseFloat(tableComputedStyle.fontSize);
                if (tableFontSizePx && !isNaN(tableFontSizePx)) {
                  // Convert px to pt (1px ≈ 0.75pt)
                  tableFontSize = Math.round(tableFontSizePx * 0.75);
                }
                
                // Dynamically extract border color and width from multiple cells to get the most common style
                if (firstRow) {
                  const firstRowCells = Array.from(firstRow.querySelectorAll('th, td'));
                  const borderColors = [];
                  const borderWidths = [];
                  
                  // Check all cells in first row to find the most common border style
                  firstRowCells.forEach(cell => {
                    const cellStyle = window.getComputedStyle(cell);
                    
                    // Check all border sides
                    const borderTopColor = cellStyle.borderTopColor;
                    const borderRightColor = cellStyle.borderRightColor;
                    const borderBottomColor = cellStyle.borderBottomColor;
                    const borderLeftColor = cellStyle.borderLeftColor;
                    
                    // Collect all non-transparent border colors
                    [borderTopColor, borderRightColor, borderBottomColor, borderLeftColor].forEach(borderColor => {
                      if (borderColor && borderColor !== 'rgba(0, 0, 0, 0)' && borderColor !== 'rgba(0,0,0,0)' && borderColor !== 'transparent') {
                        const hexBorder = rgbToHex(borderColor);
                        if (hexBorder) {
                          borderColors.push(hexBorder);
                        }
                      }
                    });
                    
                    // Collect all border widths
                    const borderTopWidth = parseFloat(cellStyle.borderTopWidth);
                    const borderRightWidth = parseFloat(cellStyle.borderRightWidth);
                    const borderBottomWidth = parseFloat(cellStyle.borderBottomWidth);
                    const borderLeftWidth = parseFloat(cellStyle.borderLeftWidth);
                    
                    [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth].forEach(width => {
                      if (width && !isNaN(width) && width > 0) {
                        borderWidths.push(width);
                      }
                    });
                  });
                  
                  // Use the most common border color (or first if all are unique)
                  if (borderColors.length > 0) {
                    // Count occurrences
                    const colorCounts = {};
                    borderColors.forEach(color => {
                      colorCounts[color] = (colorCounts[color] || 0) + 1;
                    });
                    // Get the most common color
                    tableBorderColor = Object.keys(colorCounts).reduce((a, b) => 
                      colorCounts[a] > colorCounts[b] ? a : b
                    );
                  }
                  
                  // Use the most common border width (or average if varied)
                  if (borderWidths.length > 0) {
                    const avgWidth = borderWidths.reduce((sum, w) => sum + w, 0) / borderWidths.length;
                    tableBorderWidth = Math.max(1, Math.round(avgWidth));
                  }
                  
                  // If still no border info, try to get from table element itself
                  if (!tableBorderColor || !tableBorderWidth) {
                    const tableBorderColorStyle = tableComputedStyle.borderColor || tableComputedStyle.borderTopColor;
                    if (tableBorderColorStyle && tableBorderColorStyle !== 'rgba(0, 0, 0, 0)' && tableBorderColorStyle !== 'transparent') {
                      const hexBorder = rgbToHex(tableBorderColorStyle);
                      if (hexBorder) {
                        tableBorderColor = hexBorder;
                      }
                    }
                    const tableBorderWidthStyle = parseFloat(tableComputedStyle.borderWidth || tableComputedStyle.borderTopWidth);
                    if (tableBorderWidthStyle && !isNaN(tableBorderWidthStyle) && tableBorderWidthStyle > 0) {
                      tableBorderWidth = Math.max(1, Math.round(tableBorderWidthStyle));
                    }
                  }
                  
                  // Extract column widths
                  const cells = Array.from(firstRow.querySelectorAll('th, td'));
                  cells.forEach((cell) => {
                    const computedStyle = window.getComputedStyle(cell);
                    const width = parseFloat(computedStyle.width) || cell.offsetWidth;
                    const colSpan = parseInt(cell.getAttribute('colspan') || '1');
                    
                    // For cells with colspan, divide the width equally
                    const cellWidth = width / colSpan;
                    for (let i = 0; i < colSpan; i++) {
                      originalColWidths.push(cellWidth);
                      totalOriginalWidth += cellWidth;
                    }
                  });
                }
                
                // Calculate table dimensions
                const maxCols = Math.max(...tableData.map(row => {
                  // Calculate actual columns considering colspan
                  let cols = 0;
                  row.forEach(cell => {
                    cols += (cell.options?.colspan || 1);
                  });
                  return cols;
                }));
                
                // If we have original column widths, use them; otherwise use equal widths
                // All measurements in cm, converted to inches for PptxGenJS
                // Standard slide width is 25.4 cm (10 inches), usable width is ~22.86 cm (9 inches)
                let colWidths;
                if (originalColWidths.length === maxCols && totalOriginalWidth > 0) {
                  // Scale column widths proportionally to fit slide width (22.86 cm = 9 inches)
                  // Convert pixels to cm (assuming 96 DPI: 1 inch = 96 pixels, 1 inch = 2.54 cm)
                  const usableWidthCm = 22.86; // 9 inches = 22.86 cm
                  const scaleFactor = usableWidthCm / (totalOriginalWidth / 96 * 2.54);
                  colWidths = originalColWidths.map(width => cmToInches((width / 96 * 2.54) * scaleFactor));
                } else {
                  // Fallback to equal widths if we couldn't extract original widths
                  const colWidth = cmToInches(22.86 / maxCols); // 9 inches = 22.86 cm
                  colWidths = Array(maxCols).fill(colWidth);
                }
                
                // Detect header rows (check for th elements or bold text in first few rows)
                // Typically headers are in the first 1-2 rows
                const headerRows = [];
                let headerRowCount = 0;
                
                // Check first 2 rows to see if they're headers
                for (let i = 0; i < Math.min(2, tableRows.length); i++) {
                  const row = tableRows[i];
                  const hasTh = row.querySelector('th') !== null;
                  const isBold = tableData[i] && tableData[i].some(cell => cell.options?.bold);
                  
                  // If this row has th elements or is bold, consider it a header
                  if (hasTh || isBold) {
                    headerRows.push(tableData[i]);
                    headerRowCount++;
                  } else {
                    // Once we find a non-header row, stop checking
                    break;
                  }
                }
                
                // All remaining rows are data rows
                const dataRows = tableData.slice(headerRowCount);
                
                // Calculate rows per slide based on table width
                // All measurements in cm
                // Available height: ~9.9655 cm (11.7475 - 1.778) where 11.7475 cm = 4.625 inches, 1.778 cm = 0.7 inches
                // For wide tables (many columns), rows need more vertical space due to text wrapping
                // Base row height: 0.9 cm (reduced to fit more rows), adjust based on column count
                const baseRowHeight = 0.9; // Reduced from 1.016 cm to fit more rows
                const rowHeightMultiplier = maxCols > 10 ? 1.2 : maxCols > 6 ? 1.1 : 1.0;
                const adjustedRowHeight = baseRowHeight * rowHeightMultiplier;
                
                // Calculate how many data rows can fit
                // Title area: title at 0.5 cm + title height 1.5 cm + spacing 0.3 cm = 2.3 cm
                // Table starts at 2.3 cm, so available height is from 2.3 cm to bottom (11.7475 cm)
                const availableHeight = 11.7475 - 2.3; // Total height minus title area (in cm)
                const headerHeight = headerRows.length * adjustedRowHeight;
                const dataRowsHeight = availableHeight - headerHeight;
                const maxRowsPerSlide = Math.max(12, Math.floor(dataRowsHeight / adjustedRowHeight)); // At least 12 rows
                
                // Split data into chunks
                const chunks = [];
                for (let i = 0; i < dataRows.length; i += maxRowsPerSlide) {
                  chunks.push(dataRows.slice(i, i + maxRowsPerSlide));
                }
                
                // If no chunks (empty data but has header), create one chunk with just header
                if (chunks.length === 0 && headerRows.length > 0) {
                  chunks.push([]);
                }
                
                // Create slides for each chunk
                try {
                  chunks.forEach((chunk, chunkIndex) => {
                    const slide = chunkIndex === 0 ? tableSlide : pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });
                    
                    // Add title (with page number only on subsequent slides if multiple slides)
                    const titleText = chunks.length > 1 && chunkIndex > 0
                      ? `${container.title} (Page ${chunkIndex + 1} of ${chunks.length})`
                      : container.title;
                    
                    // All measurements in cm, converted to inches for PptxGenJS
                    // Table title uses same consistent styling as chart title
                    slide.addText(titleText, titleStyle);
                    
                    // Prepare table data for this slide (include all header rows + chunk data)
                    const slideTableData = [];
                    slideTableData.push(...headerRows);
                    slideTableData.push(...chunk);
                    
                    if (slideTableData.length > 0) {
                      // Calculate total width of scaled columns
                      const totalScaledWidth = colWidths.reduce((sum, width) => sum + width, 0);
                      
                      // Validate column widths - ensure all are positive numbers
                      const validColWidths = colWidths.map(w => {
                        if (typeof w !== 'number' || isNaN(w) || !isFinite(w) || w <= 0) {
                          return cmToInches(1.0); // Default width if invalid
                        }
                        return Math.max(0.1, w); // Ensure minimum width
                      });
                      
                      // Validate total width
                      const validTotalWidth = Math.max(0.1, totalScaledWidth);
                      
                      // Build table options - use minimal defaults, let cell styles take precedence
                      // Title ends at 0.5 + 1.5 = 2.0 cm, table starts at 2.3 cm with 0.3 cm spacing
                      const tableOptions = {
                        x: cmToInches(1.27), // 0.5 inches = 1.27 cm
                        y: cmToInches(2.3), // Fixed position below title: 2.3 cm (0.906 inches)
                        w: validTotalWidth,
                        colW: validColWidths,
                      };
                      
                      // Set global border - extract dynamically from table, only use fallback if nothing found
                      // PptxGenJS needs table-level border for structure
                      if (tableBorderColor && /^[0-9A-Fa-f]{6}$/i.test(tableBorderColor) && tableBorderWidth && !isNaN(tableBorderWidth) && isFinite(tableBorderWidth) && tableBorderWidth > 0 && tableBorderWidth <= 10) {
                        tableOptions.border = { 
                          type: "solid", 
                          color: tableBorderColor.toUpperCase(), 
                          pt: Math.max(1, Math.min(10, Math.round(tableBorderWidth)))
                        };
                      } else {
                        // Try to extract border from table data cells as last resort
                        let fallbackBorderColor = null;
                        let fallbackBorderWidth = 1;
                        
                        // Check first few cells in the table data for border info
                        for (let rowIdx = 0; rowIdx < Math.min(3, slideTableData.length); rowIdx++) {
                          const row = slideTableData[rowIdx];
                          if (row && row.length > 0) {
                            const firstCell = tableRows[rowIdx]?.querySelector('th, td');
                            if (firstCell) {
                              const cellStyle = window.getComputedStyle(firstCell);
                              const borderColor = cellStyle.borderColor || cellStyle.borderTopColor;
                              if (borderColor && borderColor !== 'rgba(0, 0, 0, 0)' && borderColor !== 'transparent') {
                                const hexBorder = rgbToHex(borderColor);
                                if (hexBorder) {
                                  fallbackBorderColor = hexBorder;
                                  const borderWidth = parseFloat(cellStyle.borderWidth || cellStyle.borderTopWidth);
                                  if (borderWidth && !isNaN(borderWidth) && borderWidth > 0) {
                                    fallbackBorderWidth = Math.max(1, Math.round(borderWidth));
                                  }
                                  break;
                                }
                              }
                            }
                          }
                        }
                        
                        // Use extracted fallback or minimal default only if absolutely nothing found
                        tableOptions.border = { 
                          type: "solid", 
                          color: (fallbackBorderColor || "CCCCCC").toUpperCase(), 
                          pt: fallbackBorderWidth
                        };
                      }
                      
                      // Set default font size only if cells don't have individual sizes
                      // Extract font size dynamically from table if not already extracted
                      const hasCellFontSizes = slideTableData.some(row => 
                        row.some(cell => cell.options?.fontSize)
                      );
                      
                      if (!hasCellFontSizes) {
                        // If we didn't extract font size earlier, try to get it from table or cells
                        if (!tableFontSize || tableFontSize <= 0) {
                          // Try to get from first visible cell
                          const firstVisibleCell = tableElement.querySelector('th, td');
                          if (firstVisibleCell) {
                            const cellStyle = window.getComputedStyle(firstVisibleCell);
                            const cellFontSizePx = parseFloat(cellStyle.fontSize);
                            if (cellFontSizePx && !isNaN(cellFontSizePx)) {
                              tableFontSize = Math.round(cellFontSizePx * 0.75);
                            }
                          }
                        }
                        
                        // Only set if we have a valid extracted font size
                        if (tableFontSize && tableFontSize > 0 && tableFontSize <= 400) {
                          tableOptions.fontSize = Math.round(tableFontSize);
                        }
                      }
                      
                      // Don't set default align/valign - let cell-level options handle it
                      // This preserves individual cell alignments
                      
                      // Validate and clean table data before adding - critical for preventing corruption
                      const validatedTableData = slideTableData.map(row => {
                        if (!Array.isArray(row)) {
                          return [{ text: '' }];
                        }
                        return row.map(cell => {
                          // Ensure cell is a valid object
                          if (!cell || typeof cell !== 'object' || Array.isArray(cell)) {
                            return { text: '' };
                          }
                          
                          // Create a clean cell object with validated text
                          const cleanCell = {
                            text: typeof cell.text !== 'undefined' && cell.text !== null 
                              ? String(cell.text).replace(/\0/g, '').substring(0, 10000) 
                              : ''
                          };
                          
                          // Clean and validate options
                          if (cell.options && typeof cell.options === 'object' && !Array.isArray(cell.options)) {
                            const cleanOptions = {};
                            
                            Object.keys(cell.options).forEach(key => {
                              const value = cell.options[key];
                              
                              // Skip invalid values
                              if (value === null || value === undefined) return;
                              
                              switch (key) {
                                case 'fill':
                                case 'color':
                                  if (typeof value === 'string' && /^[0-9A-Fa-f]{6}$/i.test(value)) {
                                    cleanOptions[key] = value.toUpperCase();
                                  }
                                  break;
                                case 'fontSize':
                                  if (typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0 && value <= 400) {
                                    cleanOptions[key] = Math.round(value);
                                  }
                                  break;
                                case 'colspan':
                                  // Validate colspan (1-100) - keep it enabled
                                  if (typeof value === 'number' && !isNaN(value) && isFinite(value) && value > 0 && value <= 100) {
                                    cleanOptions[key] = Math.round(value);
                                  }
                                  break;
                                case 'rowspan':
                                  // Rowspan - DISABLED: Can cause corruption in PptxGenJS
                                  // Skip rowspan to prevent file corruption
                                  break;
                                case 'bold':
                                case 'italic':
                                  if (typeof value === 'boolean') {
                                    cleanOptions[key] = value;
                                  }
                                  break;
                                case 'align':
                                  if (['left', 'center', 'right'].includes(value)) {
                                    cleanOptions[key] = value;
                                  }
                                  break;
                                case 'valign':
                                  if (['top', 'middle', 'bottom'].includes(value)) {
                                    cleanOptions[key] = value;
                                  }
                                  break;
                                case 'fontFace':
                                  if (typeof value === 'string' && value.length > 0 && value.length <= 50) {
                                    cleanOptions[key] = value.substring(0, 50).replace(/[^\x20-\x7E]/g, ''); // Remove non-printable chars
                                  }
                                  break;
                                case 'border':
                                  // Border object: { type: "solid", color: "CCCCCC", pt: 1 }
                                  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                    const borderType = value.type === 'solid' ? 'solid' : 'solid'; // Default to solid
                                    const borderColor = (typeof value.color === 'string' && /^[0-9A-Fa-f]{6}$/i.test(value.color)) 
                                      ? value.color.toUpperCase() 
                                      : 'CCCCCC'; // Default gray
                                    const borderPt = (typeof value.pt === 'number' && !isNaN(value.pt) && isFinite(value.pt) && value.pt > 0 && value.pt <= 10)
                                      ? Math.max(0.5, Math.min(10, value.pt))
                                      : 1; // Default 1pt
                                    
                                    cleanOptions[key] = {
                                      type: borderType,
                                      color: borderColor,
                                      pt: borderPt
                                    };
                                  }
                                  break;
                                case 'margin':
                                  // Margin - DISABLED: Can cause corruption, skip it
                                  break;
                                default:
                                  // Skip unknown options to prevent corruption
                                  break;
                              }
                            });
                            
                            if (Object.keys(cleanOptions).length > 0) {
                              cleanCell.options = cleanOptions;
                            }
                          }
                          
                          return cleanCell;
                        }).filter(cell => cell !== null && cell !== undefined); // Remove any null/undefined cells
                      }).filter(row => Array.isArray(row) && row.length > 0); // Remove empty rows
                      
                      // Final validation: ensure all rows have the same number of columns
                      if (validatedTableData.length > 0) {
                        const expectedCols = Math.max(...validatedTableData.map(row => {
                          let cols = 0;
                          row.forEach(cell => {
                            cols += (cell.options?.colspan || 1);
                          });
                          return cols;
                        }));
                        
                        // Pad rows that are too short and ensure all cells are valid
                        const finalTableData = validatedTableData.map(row => {
                          if (!Array.isArray(row) || row.length === 0) {
                            // Return a row with empty cells if row is invalid
                            return Array(expectedCols).fill({ text: '' });
                          }
                          
                          let rowCols = 0;
                          const validRow = row.map(cell => {
                            // Ensure each cell is valid
                            if (!cell || typeof cell !== 'object' || Array.isArray(cell)) {
                              return { text: '' };
                            }
                            const colspan = (cell.options?.colspan || 1);
                            rowCols += colspan;
                            return cell;
                          });
                          
                          if (rowCols < expectedCols) {
                            const paddedRow = [...validRow];
                            for (let i = rowCols; i < expectedCols; i++) {
                              paddedRow.push({ text: '' });
                            }
                            return paddedRow;
                          }
                          return validRow;
                        }).filter(row => Array.isArray(row) && row.length > 0); // Final safety check
                        
                        // Ensure we have valid data before attempting to add table
                        if (finalTableData.length === 0 || finalTableData[0].length === 0) {
                          console.warn('No valid table data after validation');
                          return; // Skip this chunk
                        }
                        
                        // Final validation: ensure all rows have exactly the same number of columns
                        const firstRowColCount = finalTableData[0].length;
                        const allRowsSameLength = finalTableData.every(row => {
                          if (!Array.isArray(row)) return false;
                          // Calculate actual column count accounting for colspan
                          let colCount = 0;
                          row.forEach(cell => {
                            if (cell && typeof cell === 'object' && !Array.isArray(cell)) {
                              colCount += (cell.options?.colspan || 1);
                            } else {
                              colCount += 1;
                            }
                          });
                          return colCount === expectedCols;
                        });
                        
                        if (!allRowsSameLength) {
                          console.warn('Table rows have inconsistent column counts, fixing...');
                          // Fix rows that don't match
                          const fixedTableData = finalTableData.map(row => {
                            let rowCols = 0;
                            const validCells = row.filter(cell => {
                              if (!cell || typeof cell !== 'object' || Array.isArray(cell)) {
                                return false;
                              }
                              rowCols += (cell.options?.colspan || 1);
                              return true;
                            });
                            
                            if (rowCols < expectedCols) {
                              const fixedRow = [...validCells];
                              for (let i = rowCols; i < expectedCols; i++) {
                                fixedRow.push({ text: '' });
                              }
                              return fixedRow;
                            }
                            return validCells;
                          });
                          
                          // Use fixed data
                          finalTableData.length = 0;
                          finalTableData.push(...fixedTableData);
                        }
                        
                        // Validate tableOptions before use
                        if (!tableOptions.w || tableOptions.w <= 0 || !Array.isArray(tableOptions.colW) || tableOptions.colW.length === 0) {
                          console.warn('Invalid table options, skipping');
                          return; // Skip this chunk
                        }
                        
                        // Ensure colW array matches expected column count
                        if (tableOptions.colW.length !== expectedCols) {
                          // Adjust column widths to match
                          if (tableOptions.colW.length < expectedCols) {
                            const avgWidth = tableOptions.colW.reduce((sum, w) => sum + w, 0) / tableOptions.colW.length;
                            for (let i = tableOptions.colW.length; i < expectedCols; i++) {
                              tableOptions.colW.push(avgWidth || cmToInches(2));
                            }
                          } else {
                            tableOptions.colW = tableOptions.colW.slice(0, expectedCols);
                          }
                        }
                        
                        // Final safety check: ensure all cells are valid objects
                        const sanitizedTableData = finalTableData.map(row => {
                          if (!Array.isArray(row) || row.length === 0) {
                            // Return empty row if invalid
                            return Array(expectedCols).fill({ text: '' });
                          }
                          
                          return row.map(cell => {
                            // Ensure cell is a plain object with text property
                            if (!cell || typeof cell !== 'object' || Array.isArray(cell)) {
                              return { text: '' };
                            }
                            
                            // Create a clean cell object with sanitized text
                            // Remove control characters and null bytes that can corrupt PPTX
                            let cleanText = typeof cell.text === 'string' ? cell.text : '';
                            cleanText = cleanText
                              .replace(/\0/g, '') // Remove null bytes
                              .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, '') // Remove control characters
                              .substring(0, 10000); // Limit length
                            
                            const cleanCell = {
                              text: cleanText
                            };
                            
                            // Only include known safe options
                            if (cell.options && typeof cell.options === 'object' && !Array.isArray(cell.options)) {
                              const safeOptions = {};
                              
                              // Only include explicitly supported options
                              if (cell.options.fill && /^[0-9A-Fa-f]{6}$/i.test(cell.options.fill)) {
                                safeOptions.fill = cell.options.fill.toUpperCase();
                              }
                              if (cell.options.color && /^[0-9A-Fa-f]{6}$/i.test(cell.options.color)) {
                                safeOptions.color = cell.options.color.toUpperCase();
                              }
                              if (typeof cell.options.fontSize === 'number' && !isNaN(cell.options.fontSize) && isFinite(cell.options.fontSize) && cell.options.fontSize > 0 && cell.options.fontSize <= 400) {
                                safeOptions.fontSize = Math.round(cell.options.fontSize);
                              }
                              // Colspan - always include if present and valid (even if it's 1)
                              // PptxGenJS needs colspan to be explicitly set for proper table structure
                              if (cell.options.colspan !== undefined && typeof cell.options.colspan === 'number' && !isNaN(cell.options.colspan) && isFinite(cell.options.colspan) && cell.options.colspan > 0 && cell.options.colspan <= 100) {
                                safeOptions.colspan = Math.round(cell.options.colspan);
                              }
                              if (typeof cell.options.bold === 'boolean') {
                                safeOptions.bold = cell.options.bold;
                              }
                              if (typeof cell.options.italic === 'boolean') {
                                safeOptions.italic = cell.options.italic;
                              }
                              if (['left', 'center', 'right'].includes(cell.options.align)) {
                                safeOptions.align = cell.options.align;
                              }
                              if (['top', 'middle', 'bottom'].includes(cell.options.valign)) {
                                safeOptions.valign = cell.options.valign;
                              }
                              if (typeof cell.options.fontFace === 'string' && cell.options.fontFace.length > 0 && cell.options.fontFace.length <= 50) {
                                safeOptions.fontFace = cell.options.fontFace.substring(0, 50).replace(/[^\x20-\x7E]/g, '');
                              }
                              // Border support
                              if (cell.options.border && typeof cell.options.border === 'object' && cell.options.border !== null && !Array.isArray(cell.options.border)) {
                                const border = cell.options.border;
                                const borderType = border.type === 'solid' ? 'solid' : 'solid';
                                const borderColor = (typeof border.color === 'string' && /^[0-9A-Fa-f]{6}$/i.test(border.color)) 
                                  ? border.color.toUpperCase() 
                                  : 'CCCCCC';
                                const borderPt = (typeof border.pt === 'number' && !isNaN(border.pt) && isFinite(border.pt) && border.pt > 0 && border.pt <= 10)
                                  ? Math.max(0.5, Math.min(10, border.pt))
                                  : 1;
                                
                                safeOptions.border = {
                                  type: borderType,
                                  color: borderColor,
                                  pt: borderPt
                                };
                              }
                              
                              if (Object.keys(safeOptions).length > 0) {
                                cleanCell.options = safeOptions;
                              }
                            }
                            
                            return cleanCell;
                          }).filter(cell => cell !== null && cell !== undefined); // Remove any null cells
                        }).filter(row => Array.isArray(row) && row.length > 0); // Remove empty rows
                        
                        // Final validation before adding to slide
                        if (!sanitizedTableData || sanitizedTableData.length === 0 || !Array.isArray(sanitizedTableData[0]) || sanitizedTableData[0].length === 0) {
                          console.warn('Sanitized table data is invalid, skipping');
                          return; // Skip this chunk
                        }
                        
                        // Ensure all rows have the same number of cells (accounting for colspan)
                        const firstRowCellCount = sanitizedTableData[0].reduce((sum, cell) => sum + (cell.options?.colspan || 1), 0);
                        const allRowsValid = sanitizedTableData.every(row => {
                          const rowCellCount = row.reduce((sum, cell) => sum + (cell.options?.colspan || 1), 0);
                          return rowCellCount === firstRowCellCount;
                        });
                        
                        if (!allRowsValid) {
                          console.warn('Table rows still have inconsistent column counts after sanitization, skipping');
                          return; // Skip this chunk to prevent corruption
                        }
                        
                        try {
                          slide.addTable(sanitizedTableData, tableOptions);
                        } catch (tableAddError) {
                          console.error('Error adding table to slide:', tableAddError);
                          // Fallback: try with minimal options but preserve cell styles
                          // Extract border dynamically even in fallback
                          let fallbackBorderColor = tableBorderColor || "CCCCCC";
                          let fallbackBorderWidth = tableBorderWidth || 1;
                          
                          // Validate fallback border color
                          if (!/^[0-9A-Fa-f]{6}$/i.test(fallbackBorderColor)) {
                            fallbackBorderColor = "CCCCCC";
                          }
                          if (!fallbackBorderWidth || isNaN(fallbackBorderWidth) || fallbackBorderWidth <= 0) {
                            fallbackBorderWidth = 1;
                          }
                          
                          // Try one more time to extract from table
                          if (!tableBorderColor || !tableBorderWidth) {
                            const firstCell = tableElement.querySelector('th, td');
                            if (firstCell) {
                              const cellStyle = window.getComputedStyle(firstCell);
                              const borderColor = cellStyle.borderColor || cellStyle.borderTopColor;
                              if (borderColor && borderColor !== 'rgba(0, 0, 0, 0)' && borderColor !== 'transparent') {
                                const hexBorder = rgbToHex(borderColor);
                                if (hexBorder && /^[0-9A-Fa-f]{6}$/i.test(hexBorder)) {
                                  fallbackBorderColor = hexBorder;
                                }
                              }
                              const borderWidth = parseFloat(cellStyle.borderWidth || cellStyle.borderTopWidth);
                              if (borderWidth && !isNaN(borderWidth) && isFinite(borderWidth) && borderWidth > 0) {
                                fallbackBorderWidth = Math.max(1, Math.min(10, Math.round(borderWidth)));
                              }
                            }
                          }
                          
                          // Validate column widths for fallback
                          const validFallbackColWidths = colWidths.map(w => {
                            if (typeof w !== 'number' || isNaN(w) || !isFinite(w) || w <= 0) {
                              return cmToInches(1.0);
                            }
                            return Math.max(0.1, w);
                          });
                          
                          // Create minimal options with validated values
                          const minimalOptions = {
                            x: cmToInches(1.27),
                            y: cmToInches(2.3), // Fixed position below title: 2.3 cm
                            w: Math.max(0.1, validTotalWidth || cmToInches(10)), // Ensure width is positive
                            colW: validFallbackColWidths.length > 0 ? validFallbackColWidths : [cmToInches(2)], // Ensure at least one column
                            border: { 
                              type: "solid", 
                              color: fallbackBorderColor.toUpperCase(), 
                              pt: Math.max(1, Math.min(10, Math.round(fallbackBorderWidth)))
                            },
                          };
                          
                          // Try with minimal options - if this fails, we'll fall through to image
                          try {
                            slide.addTable(sanitizedTableData, minimalOptions);
                          } catch (minimalError) {
                            console.error('Error with minimal options:', minimalError);
                            throw minimalError; // Re-throw to trigger image fallback
                          }
                        }
                      } else {
                        // If no validated data, skip this chunk
                        console.warn('No valid table data for chunk');
                      }
                    }
                  });
                } catch (tableError) {
                  console.error('Error adding table to PPTX:', tableError);
                  // Fallback to image if table creation fails
                  const tableImage = await html2canvas(container.element, {
                    backgroundColor: "#FFFFFF",
                    scale: 2,
                    logging: false,
                  });
                  addImageWithTitleToSlide(
                    tableSlide,
                    tableImage.toDataURL(),
                    tableImage.width,
                    tableImage.height,
                    container.title
                  );
                }
              } else {
                // Fallback to image if table extraction fails
                const image = await html2canvas(container.element, {
                  backgroundColor: "#FFFFFF",
                  scale: 2,
                  logging: false,
                });
                const slide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });
                addImageWithTitleToSlide(
                  slide,
                  image.toDataURL(),
                  image.width,
                  image.height,
                  container.title
                );
              }
            } else {
              // No table element found, export as image
              const image = await html2canvas(container.element, {
                backgroundColor: "#FFFFFF",
                scale: 2,
                logging: false,
              });
              const slide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });
              addImageWithTitleToSlide(
                slide,
                image.toDataURL(),
                image.width,
                image.height,
                container.title
              );
            }
          } else {
            // Export chart as image
            const image = await html2canvas(container.element, {
              backgroundColor: "#FFFFFF",
              scale: 2,
              logging: false,
            });

            const slide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });
            addImageWithTitleToSlide(
              slide,
              image.toDataURL(),
              image.width,
              image.height,
              container.title
            );
          }
        } catch (error) {
          console.error(`Error capturing ${container.type}:`, error);
          // Continue with next item
        }
      }

      // Save the PowerPoint presentation with date and time
      const finalFileName = fileName || dashboardTitle || "Dashboard";
      
      // Generate date and time string: YYYY-MM-DD_HH-mm-ss
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const dateTimeString = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
      
      await pptx.writeFile(`SkyGeni-${finalFileName}-${dateTimeString}.pptx`);

      setLoading(false);
      setTooltipText("All charts and tables downloaded successfully!");

      setTimeout(() => {
        setTooltipText("Download all charts and tables as PowerPoint");
      }, 5000);
    } catch (error) {
      console.error("Error exporting dashboard:", error);
      setLoading(false);
      setTooltipText("Error exporting. Please try again.");
      setTimeout(() => {
        setTooltipText("Download all charts and tables as PowerPoint");
      }, 3000);
    }
  };

  const getTitleFromContainer = (container) => {
    // Try to find title in various ways
    // Look for Typography with variant="h2", h5, h6 which are used in different components
    const titleElement = container.querySelector('h2') || 
                        container.querySelector('h5') ||
                        container.querySelector('h6') ||
                        container.querySelector('[class*="MuiTypography-h2"]') ||
                        container.querySelector('[class*="MuiTypography-h5"]') ||
                        container.querySelector('[class*="MuiTypography-h6"]') ||
                        container.closest('[class*="MuiGrid"]')?.querySelector('h2') ||
                        container.closest('[class*="MuiGrid"]')?.querySelector('h5') ||
                        container.closest('[class*="MuiGrid"]')?.querySelector('h6') ||
                        container.closest('[class*="MuiGrid"]')?.querySelector('[class*="MuiTypography-h2"]') ||
                        container.closest('[class*="MuiGrid"]')?.querySelector('[class*="MuiTypography-h5"]') ||
                        container.closest('[class*="MuiGrid"]')?.querySelector('[class*="MuiTypography-h6"]');
    
    if (titleElement) {
      return titleElement.textContent.trim();
    }
    
    // Look in parent containers (Paper, Card, etc.)
    let parent = container.parentElement;
    for (let i = 0; i < 5 && parent; i++) {
      const title = parent.querySelector('h2') || 
                   parent.querySelector('h5') ||
                   parent.querySelector('h6') ||
                   parent.querySelector('[class*="MuiTypography-h2"]') ||
                   parent.querySelector('[class*="MuiTypography-h5"]') ||
                   parent.querySelector('[class*="MuiTypography-h6"]');
      if (title) {
        return title.textContent.trim();
      }
      parent = parent.parentElement;
    }
    
    // If still no title found, try to get from Paper component's header
    const paperElement = container.closest('[class*="MuiPaper"]');
    if (paperElement) {
      const paperTitle = paperElement.querySelector('h2') || 
                        paperElement.querySelector('h5') ||
                        paperElement.querySelector('h6') ||
                        paperElement.querySelector('[class*="MuiTypography-h2"]') ||
                        paperElement.querySelector('[class*="MuiTypography-h5"]') ||
                        paperElement.querySelector('[class*="MuiTypography-h6"]');
      if (paperTitle) {
        return paperTitle.textContent.trim();
      }
    }
    
    return null;
  };

  return (
    <CustomWidthTooltip title={tooltipText} placement="left">
      <Button
        onClick={handleExport}
        disabled={loading}
        variant="contained"
        color="primary"
        startIcon={<GetAppIcon />}
        style={{
          margin: "1rem",
          position: "fixed",
          top: "80px",
          right: "20px",
          zIndex: 1000,
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
      >
        {loading ? "Exporting..." : "Download All"}
      </Button>
    </CustomWidthTooltip>
  );
}

export default GlobalDownloadButton;

